import { type RefObject, useCallback, useRef } from 'react'
import { getElementTopFromScrollBoxTop, getItemById } from './scrollAnchor'

type StartAnchorLockParams = {
  /** Value of `itemAttribute` on the row that should stay visually pinned. */
  anchorId: string
  /** Distance (px) from the scroll container's top edge to the anchor's top. */
  anchorTopFromScrollBoxTop: number
}

type UseScrollAnchorLock = (params: {
  scrollRef: RefObject<HTMLElement | null>
  /** Data attribute on paginatable rows, e.g. `data-follow-up-message-id`. */
  itemAttribute: string
}) => {
  /** Begin pinning the given anchor at its captured offset across re-renders. */
  start: (params: StartAnchorLockParams) => void
  /** Cancel a running lock (e.g. on unmount, on case switch, on fetch error). */
  stop: () => void
}

/**
 * RAF loop that keeps a chosen row at a fixed offset from the scroll
 * container's top across re-renders — used by "Show more" paging in chat and
 * audit log. Each frame:
 *
 *  - looks up the anchor element (cached after the first successful lookup)
 *  - measures its current top vs. the captured `anchorTopFromScrollBoxTop`
 *  - nudges `scrollTop` by the delta if it's >= 0.5px
 *
 * Exits when:
 *  - the anchor sits at the target offset for 8 frames in a row (stable)
 *  - the anchor stays missing from the DOM for 60 frames (gone for good)
 *  - the user scrolls UP (scrollTop drops > 5px below our last write). We
 *    explicitly only treat *decreases* as user intent; `overflow-anchor:
 *    auto` can legitimately INCREASE scrollTop on its own as content grows
 *    above the viewport, and that's not a user action.
 *  - 240 frames (~4s) have elapsed regardless of state — hard cap so the
 *    loop can't leak when native scroll anchoring already compensated the
 *    prepend before the first tick (delta < 0.5 from frame 1, which would
 *    otherwise keep `sawMovement` false and block the stable-frames exit).
 */
export const useScrollAnchorLock: UseScrollAnchorLock = ({ scrollRef, itemAttribute }) => {
  const frameIdRef = useRef<number | null>(null)

  const stop = useCallback(() => {
    if (frameIdRef.current !== null) {
      cancelAnimationFrame(frameIdRef.current)
      frameIdRef.current = null
    }
  }, [])

  const start = useCallback(
    ({ anchorId, anchorTopFromScrollBoxTop }: StartAnchorLockParams) => {
      stop()
      const STABLE_FRAMES = 8
      const MISSING_GRACE_FRAMES = 60
      const MAX_FRAMES = 240 // ~4s hard cap — bail even if movement is never observed
      let stableFrames = 0
      let missingFrames = 0
      let framesElapsed = 0
      let sawMovement = false
      let lastWrittenScrollTop: number | null = null
      let cachedAnchor: HTMLElement | null = null

      const tick = () => {
        framesElapsed++
        const scrollEl = scrollRef.current
        if (cachedAnchor && !scrollEl?.contains(cachedAnchor)) {
          cachedAnchor = null
        }
        const anchor =
          cachedAnchor ?? (scrollEl ? getItemById(scrollEl, itemAttribute, anchorId) : null)
        if (anchor && !cachedAnchor) {
          cachedAnchor = anchor
        }

        if (
          scrollEl &&
          lastWrittenScrollTop !== null &&
          scrollEl.scrollTop < lastWrittenScrollTop - 5
        ) {
          frameIdRef.current = null
          return
        }

        if (!scrollEl || !anchor) {
          missingFrames++
          if (missingFrames >= MISSING_GRACE_FRAMES) {
            frameIdRef.current = null
            return
          }
        } else {
          missingFrames = 0
          const delta = getElementTopFromScrollBoxTop(scrollEl, anchor) - anchorTopFromScrollBoxTop
          if (Math.abs(delta) < 0.5) {
            if (sawMovement) {
              stableFrames++
            }
          } else {
            sawMovement = true
            stableFrames = 0
            scrollEl.scrollTop += delta
            lastWrittenScrollTop = scrollEl.scrollTop
          }
        }

        if (stableFrames >= STABLE_FRAMES || framesElapsed >= MAX_FRAMES) {
          frameIdRef.current = null
          return
        }
        frameIdRef.current = requestAnimationFrame(tick)
      }

      frameIdRef.current = requestAnimationFrame(tick)
    },
    [scrollRef, itemAttribute, stop]
  )

  return { start, stop }
}
